/**
 * Qualitative Item Drawer Component
 * 
 * Complete drawer solution for adding/editing qualitative factors including:
 * - Form component for qualitative factor, risk ranking, and custom comment
 * - Support for fixed rows (Fraud Risk, Overall Risk Ranking Conclusion)
 * - Form validation
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button } from 'antd';
import styled from 'styled-components';
import RightContentDrawer from '../../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../../components/common/ButtonGroup';
import { RiskRanking } from '@/services/types';
import { RootState } from '@/store';
import { useSelector } from 'react-redux';

const { TextArea } = Input;

// ========== TYPE DEFINITIONS ==========

export interface QualitativeFactorFormData {
  id?: string; // Optional id for edit mode
  qualitativeFactor: string;
  riskRanking: RiskRanking;
  customComment: string;
  isFixed?: boolean; // For Fraud Risk and Overall Risk Ranking Conclusion
}

export interface QualitativeItemDrawerProps {
  open: boolean;
  mode: 'add' | 'edit';
  initialValues?: Partial<QualitativeFactorFormData>;
  onClose: () => void;
  onSubmit: (values: QualitativeFactorFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledInput = styled(Input)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;
  height: 44px;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }

  &:disabled {
    background: #F9FAFB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;

const StyledTextArea = styled(TextArea)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;
  min-height: 120px !important;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  
  .ant-select-selector {
    height: 44px !important;
    border-radius: 8px !important;
    align-items: center;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  .ant-select-selection-item {
    line-height: 42px !important;
  }
`;

const InfoText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #6B7280;
  margin-top: 6px;
`;

// ========== MAIN COMPONENT ==========

const QualitativeItemDrawer: React.FC<QualitativeItemDrawerProps> = ({
  open,
  mode,
  initialValues,
  onClose,
  onSubmit,
}) => {
  // Form states
  const [form] = Form.useForm();
  const [qualitativeFactor, setQualitativeFactor] = useState('');
  const [riskRanking, setRiskRanking] = useState<RiskRanking>('Unranked');
  const [customComment, setCustomComment] = useState('');
  const [isFixed, setIsFixed] = useState(false);
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  // UI states
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Reset form when initialValues change
  useEffect(() => {
    if (mode === 'edit' && initialValues) {
      // Edit mode - load existing values
      setQualitativeFactor(initialValues.qualitativeFactor || '');
      setRiskRanking(initialValues.riskRanking || 'Unranked');
      setCustomComment(initialValues.customComment || '');
      setIsFixed(initialValues.isFixed || false);
      form.setFieldsValue({
        qualitativeFactor: initialValues.qualitativeFactor,
        riskRanking: initialValues.riskRanking,
        customComment: initialValues.customComment,
      });
    } else {
      // Add mode - reset to empty
      resetForm();
    }
  }, [open, mode, initialValues]);

  // Watch for field changes and validate
  useEffect(() => {
    const checkFormValidation = () => {
      const hasQualitativeFactor = !!qualitativeFactor && qualitativeFactor.trim() !== '';
      const isValid = hasQualitativeFactor;
      setIsFormValid(isValid);
    };

    checkFormValidation();
  }, [qualitativeFactor]);

  const resetForm = () => {
    setQualitativeFactor('');
    setRiskRanking('Unranked');
    setCustomComment('');
    setIsFixed(false);
    form.resetFields();
  };

  const handleClose = () => {
    setIsFormValid(false);
    resetForm();
    onClose();
  };

  const handleFormSubmit = async () => {
    setLoading(true);
    try {
      const formData: QualitativeFactorFormData = {
        qualitativeFactor,
        riskRanking,
        customComment,
        isFixed,
      };

      // Include id for edit mode
      if (mode === 'edit' && initialValues) {
        formData.id = initialValues.id;
      }

      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error("Form submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const drawerTitle = mode === 'add' ? 'Add Qualitative Factor' : 'Edit Qualitative Factor';

  return (
    <RightContentDrawer
      open={open}
      title={drawerTitle}
      width={572}
      onClose={handleClose}
      footer={
        <ButtonGroup>
          <Button
            type="default"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleFormSubmit}
            loading={loading}
            disabled={!isFormValid || !isAdmin}
          >
            {loading ? 'Saving...' : mode === 'add' ? 'Add Factor' : 'Save Changes'}
          </Button>
        </ButtonGroup>
      }
    >
      <StyledForm
        id="qualitative-factor-form"
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        {/* Qualitative Factor */}
        <Form.Item
          name="qualitativeFactor"
          rules={[
            { required: true, message: 'Please enter a qualitative factor' },
            { whitespace: true, message: 'Qualitative factor cannot be empty' },
          ]}
        >
          <div>
            <FormLabel>Qualitative Factor</FormLabel>
            <StyledInput
              value={qualitativeFactor}
              onChange={(e) => setQualitativeFactor(e.target.value)}
              placeholder="Enter qualitative factor"
              disabled={isFixed}
              spellCheck={true}
            />
            {isFixed && (
              <InfoText>
                This is a fixed row and the factor name cannot be changed
              </InfoText>
            )}
          </div>
        </Form.Item>

        {/* Risk Ranking */}
        <Form.Item name="riskRanking">
          <div>
            <FormLabel>Risk Ranking</FormLabel>
            <StyledSelect
              value={riskRanking}
              onChange={(value) => setRiskRanking(value as RiskRanking)}
              placeholder="Select risk ranking"
              options={[
                { value: 'High', label: 'High' },
                { value: 'Med', label: 'Med' },
                { value: 'Low', label: 'Low' },
                { value: 'Unranked', label: 'Unranked' },
              ]}
            />
          </div>
        </Form.Item>

        {/* Comment */}
        <Form.Item name="customComment">
          <div>
            <FormLabel>Comment</FormLabel>
            <StyledTextArea
              value={customComment}
              onChange={(e) => setCustomComment(e.target.value)}
              placeholder="Enter comment regarding qualitative assessment risk ranking"
              rows={5}
              spellCheck={true}
            />
          </div>
        </Form.Item>
      </StyledForm>
    </RightContentDrawer>
  );
};

export default QualitativeItemDrawer;

